/**
 * Google Analytics 4 (gtag.js) loader.
 *
 * Activates when `VITE_GA_MEASUREMENT_ID` is set in the Vite env
 * (e.g. `G-V49JWKM8PK`). All exports are no-ops when unset, so call
 * sites don't need to guard.
 *
 * GA4 handles pageviews differently from PostHog:
 *   - On a classic multi-page site, each `<script async src="gtag.js?id=...">`
 *     auto-fires a `page_view` event on load.
 *   - On a SPA (our case) we suppress the default pageview with
 *     `send_page_view: false` and fire one manually via
 *     `trackGAPageview()` from <PageviewTracker> + the landing hash
 *     router. That way /signup, /pacioli, /brand all register as
 *     distinct pageviews instead of collapsing into one.
 *
 * Why GA4 in addition to PostHog? GA4 covers what PostHog doesn't —
 * Google Ads conversion tracking, Search Console attribution, and
 * the default dashboards every marketing contractor already knows
 * how to read. Product analytics stays in PostHog.
 *
 * No PII is sent. GA4 IP anonymisation is on by default (as of GA4;
 * the old `anonymize_ip` flag is redundant).
 */

import { CONSENT_EVENT, readConsent, type ConsentEventDetail } from './consent';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;
let consentListenerBound = false;

function gaId(): string | null {
  const id = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
  return id && id.trim() ? id.trim() : null;
}

export function initGtag(): void {
  if (initialized) return;
  const id = gaId();
  if (!id) return;
  if (typeof window === 'undefined') return;

  // GDPR gate: don't inject gtag.js until the user has accepted the
  // cookie banner. Declined → permanently dormant. Null → wait for
  // the `altai-consent` CustomEvent, then re-enter.
  const consent = readConsent();
  if (consent !== 'accepted') {
    if (consent === 'declined') return;
    if (consentListenerBound) return;
    consentListenerBound = true;
    const onConsent = (evt: Event) => {
      const detail = (evt as CustomEvent<ConsentEventDetail>).detail;
      if (detail?.value !== 'accepted') return;
      window.removeEventListener(CONSENT_EVENT, onConsent);
      initGtag();
    };
    window.addEventListener(CONSENT_EVENT, onConsent);
    return;
  }

  // Inject the official async loader. Same shape as Google's
  // published snippet — keep the inline bootstrap + the dataLayer
  // queue because `gtag('config', ...)` calls made synchronously
  // after this file loads must land in the queue before the async
  // script executes.
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };

  window.gtag('js', new Date());

  const appVersion = (import.meta.env.VITE_BUILD_ID as string | undefined) || 'dev';
  window.gtag('config', id, {
    // We drive SPA pageviews ourselves.
    send_page_view: false,
    // Custom dimension — set up a matching "app_version" custom dim
    // in GA4 → Admin → Custom definitions so this shows up in
    // reports.
    app_version: appVersion,
  });

  initialized = true;
}

/**
 * Fire a GA4 `page_view` event. Call on every SPA route change.
 * Works for both react-router (web app) and the landing hash
 * router — pass `window.location.href` each time.
 */
export function trackGAPageview(url?: string): void {
  if (!initialized) return;
  const id = gaId();
  if (!id || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_location: url ?? window.location.href,
    page_path: url ? new URL(url, window.location.origin).pathname : window.location.pathname,
    page_title: document.title,
  });
}

/**
 * Fire a custom GA4 event. Useful for conversion tracking
 * (Google Ads) — keep event names short (`signup`, `login`,
 * `purchase`, `lead`) so the GA4 UI suggests them automatically.
 */
export function trackGAEvent(event: string, params?: Record<string, unknown>): void {
  if (!initialized || !window.gtag) return;
  window.gtag('event', event, params);
}

export const gtagEnabled = (): boolean => initialized;
